// eslint-disable-next-line import/no-cycle
import {
  addTranslationsToState,
  replaceTranslationInState,
  replaceTranslationInStateAllLanguages,
  removeTranslation,
  getTranslationByKeyAndLang,
  TemplateEditorState as state,
  fixMissingTranslationsForShopLanguages,
  cleanUpTranslations,
} from '@/composables/template-editor/TemplateEditor';
// eslint-disable-next-line import/no-cycle
import {
  Lang,
  UserState,
  getShopDefaultLang,
} from '@/composables/User';
// eslint-disable-next-line import/no-cycle
import {
  Translation, Property, ActionTypeEnum, Section,
} from '@/types';
import axios from 'axios';
// eslint-disable-next-line import/no-cycle
import { br2nl, getParents, nl2br } from '@/helpers';

import * as cheerio from 'cheerio';

// eslint-disable-next-line import/no-cycle
import {
  computeAttribute,
  getTemplateIframeDocument,
  isManagedByAttribute,
  isWhatsappTemplate,
} from './helpers';
// eslint-disable-next-line import/no-cycle
import {
  ACTIONS_BUTTON_GROUP,
  TEMPLATE_SECTION_CLASS,
  TRANSLATION_ATTRIBUTE,
  TRANSLATION_MARKUP,
  TEMPLATE_TRANSLATE_SPECIFIC,
  TEMPLATE_TRANSLATE_SPECIFIC_ITEMS,
  TEMPLATE_TRANSLATE_SPECIFIC_ONLY_ATTRIBUTE,
  TEMPLATE_TRANSLATE_SPECIFIC_ONLY_CONTENT,
  TEMPLATE_TRANSLATE_SPECIFIC_HTML_CONTENT,
  TRANSLATION_EXCLUDE_IDS,
} from './constants';

// Editor-only decorators injected in the iframe (padding handles, labels, action buttons): never translatable content
const EDITOR_ONLY_CLASSES = ['spm-padding-handles-container', 'spm-padding-handles-container-row', 'spm-padding-tooltip', 'spm-label-wrapper', ACTIONS_BUTTON_GROUP];

const isEditorOnlyElement = (element: cheerio.Cheerio<cheerio.Node>): boolean => EDITOR_ONLY_CLASSES
  .some((cls) => element.hasClass(cls) || element.closest(`.${cls}`).length > 0);

// Content already processed by a previous pass: translation markup or translated attributes inside
const containsTranslatedContent = (html: string | null): boolean => !!html
  && (new RegExp(`<${TRANSLATION_MARKUP}\\b[^>]*>`, 'i').test(html) || html.includes(`${TRANSLATION_ATTRIBUTE}-`));

export const updateIframeTranslation = (translation: Translation) => {
  const template = getTemplateIframeDocument();
  if (template) {
    if (isManagedByAttribute(translation)) { // translate attribute
      const attr = computeAttribute(translation.key, translation.fieldType);
      (Array.from(template.body.querySelectorAll(`[${TRANSLATION_ATTRIBUTE}-${attr}='LANG_${translation.key}']`)) as HTMLElement[])
        .forEach((item: HTMLElement) => {
          item.setAttribute(`${attr}`, `${translation.value}`);
          // Check for data-redirect
          if (attr === 'data-redirect') {
            let hasClassRedirect = false;
            const actionTypes = Object.values(ActionTypeEnum).map((actionType) => `spm_action_${actionType}`);
            // eslint-disable-next-line no-restricted-syntax
            for (const actionType of actionTypes) {
              if (item.classList.contains(actionType)) {
                hasClassRedirect = true;
                item.classList.remove(actionType);
                break;
              }
            }
            if (hasClassRedirect && translation.value.match(new RegExp('#redirect_'))) {
              item.classList.add(`spm_action_${ActionTypeEnum.DISPLAY_TEMPLATE}`);
            } else if (hasClassRedirect && !translation.value.match(new RegExp('#redirect_'))) {
              item.classList.add(`spm_action_${ActionTypeEnum.REDIRECT}`);
            }
          }
          if (translation.fieldType === 'data-selectoptionplaceholder' || translation.fieldType === 'data-selectoptionlabel') {
            // eslint-disable-next-line no-param-reassign
            item.innerHTML = translation.value;
          }
          // For SVG text elements: also update textContent and sync data-prizes
          if (attr === 'data-spm-text') {
            item.textContent = translation.value;
            // Sync the label back into the parent widget's data-prizes JSON
            const widget = item.closest('.spm_widget_game_wheel') as any;
            if (widget) {
              try {
                const prizes = JSON.parse(widget.getAttribute('data-prizes') || '[]');
                const prize = prizes.find((p: any) => p.translationKey === translation.key);
                if (prize) {
                  prize.label = translation.value;
                  const jsonStr = JSON.stringify(prizes);
                  // Use quiet update to avoid triggering observer re-render
                  if (widget._spmGameWheel) {
                    widget._spmGameWheel.setDataPrizesQuietly(jsonStr);
                  } else {
                    widget.setAttribute('data-prizes', jsonStr);
                  }
                }
              } catch (e) {
                // data-prizes parsing failed, skip sync
              }
            }
          }
          // For slot machine source elements: update textContent and sync JSON (triggers attribute observer → refresh)
          if (attr === 'data-spm-slot-symbol') {
            item.textContent = translation.value;
            const widget = item.closest('.spm_widget_game_slot_machine');
            if (widget) {
              try {
                const symbols = JSON.parse(widget.getAttribute('data-symbols') || '[]');
                const symbol = symbols.find((s: any) => s.translationKey === translation.key);
                if (symbol) {
                  symbol.text = translation.value;
                  symbol.label = translation.value;
                  widget.setAttribute('data-symbols', JSON.stringify(symbols));
                }
              } catch (e) { /* skip */ }
            }
          }
          // Countdown label (e-mail): the GIF is derived from these attributes, so
          // editing a label or switching language must rebuild the image.
          if (attr.startsWith('data-countdown-label-')) {
            import('@/components/template-builder/setters').then(({ refreshEmailCountdownForLabel }) => {
              refreshEmailCountdownForLabel(item);
            });
          }
          if (attr === 'data-spm-slot-prize') {
            item.textContent = translation.value;
            const widget = item.closest('.spm_widget_game_slot_machine');
            if (widget) {
              try {
                const combos = JSON.parse(widget.getAttribute('data-win-combinations') || '[]');
                const combo = combos.find((c: any) => c.translationKey === translation.key);
                if (combo) {
                  combo.prize = translation.value;
                  widget.setAttribute('data-win-combinations', JSON.stringify(combos));
                }
              } catch (e) { /* skip */ }
            }
          }
        });
    }

    if (!isManagedByAttribute(translation)) { // translate content
      (Array.from(template.body.querySelectorAll(`${TRANSLATION_MARKUP}[id='LANG_${translation.key}']`)) as HTMLElement[])
        // eslint-disable-next-line no-param-reassign
        .forEach((item: HTMLElement) => {
          item.innerHTML = translation.value;
          // If this is a CTA translation, sync to carousel cards
          if (item.closest('.whatsapp-message__cta__item')) {
            syncCtasToCarouselCards(template);
          }
        });
    }
  }
};

/**
 * Sync CTAs from body message to carousel cards (for WhatsApp smartlist)
 */
const syncCtasToCarouselCards = (iframeDoc: Document) => {
  // Check if smartlist is enabled
  const carousel = iframeDoc.querySelector('.whatsapp-smartlist-carousel');
  if (!carousel?.classList.contains('smartlist-enabled')) return;

  // Get CTAs from body message
  const ctaListContainer = iframeDoc.querySelector('.whatsapp-message__cta__list');
  if (!ctaListContainer) return;

  const ctaElements = ctaListContainer.querySelectorAll('.whatsapp-message__cta__item');
  if (ctaElements.length === 0) return;

  // Get first CTA text (only 1 CTA allowed on carousel cards)
  const firstCta = ctaElements[0];
  const translationNode = firstCta.querySelector('spmtranslation');
  const ctaText = translationNode?.innerHTML || firstCta.textContent || '';

  // Update all CTA elements on carousel cards
  const carouselCtas = iframeDoc.querySelectorAll('.whatsapp-smartlist-cta');
  carouselCtas.forEach((cta) => {
    cta.textContent = ctaText;
  });
};

export const updateIframeTranslations = (translations: Translation[]) => {
  translations
    .forEach((translation: Translation) => updateIframeTranslation(translation));
};

/**
 * Search for translations in element that are not already in the state
 * @param element
 */
export const searchMissingTranslations = (element: HTMLElement): Translation[] => {
  // We search for translations in HTML not in translations object
  let translationToAddToState: Translation[] = [];
  const regExpMissingTranslationsAttributes = new RegExp(`${TRANSLATION_ATTRIBUTE}-([^=]+)="LANG_([^"]+)"`, 'g');
  const regExpMissingTranslationsMarkups = new RegExp(`<${TRANSLATION_MARKUP} id="LANG_([^"]+)">([^<]+)</${TRANSLATION_MARKUP}>`, 'g');
  const matchesTranslationsAsAttributes = element.innerHTML.matchAll(regExpMissingTranslationsAttributes);
  const matchesTranslationsAsMarkups = element.innerHTML.matchAll(regExpMissingTranslationsMarkups);

  Array.from(matchesTranslationsAsMarkups).forEach((match) => {
    const key = match[1];
    const valueOfTranslation = match[2];
    const found = translationToAddToState.filter((translation) => translation.key === key);

    if (found.length === 0) {
      // Key is not in the array of translations to add to state
      const elementWithTranslation = element.querySelector(`${TRANSLATION_MARKUP}[id="LANG_${key}"]`);

      let callback = (value: string) => value;
      let translationValue = valueOfTranslation;
      if (state.template?.type === 'sms') {
        callback = (value: string) => nl2br(value, true);
        translationValue = br2nl(valueOfTranslation);
      }

      let sectionId = 0;
      const section = elementWithTranslation?.closest('[data-spmelementid]');

      if (section) {
        sectionId = Number(section.getAttribute('data-spmelementid'));
      }

      // Create translations for each language of current shop
      const translationsToAdd: Translation[] = UserState.activeShop?.langs.map((language: Lang) => ({
        section: sectionId ?? 0,
        language: language.id,
        fieldType: 'text',
        key,
        value: translationValue,
        callback,
      })) ?? [];

      // Merge with translations to add
      translationToAddToState = [...translationToAddToState, ...translationsToAdd];
    }
  });

  // Loop on matched translations as attributes
  Array.from(matchesTranslationsAsAttributes).forEach((match) => {
    const attribute = match[1];
    const key = match[2];
    const found = translationToAddToState.filter((translation) => translation.key === key);

    if (found.length === 0) {
      // Key is not in the array of translations to add to state
      const elementWithTranslation = element.querySelector(`[${TRANSLATION_ATTRIBUTE}-${match[1]}="LANG_${key}"]`);
      let valueOfTranslation = '';

      if (elementWithTranslation && elementWithTranslation.hasAttribute(`${match[1]}`)) {
        valueOfTranslation = elementWithTranslation.getAttribute(`${match[1]}`) ?? '';
      }

      let fieldType = 'text';

      switch (attribute) {
        case 'href':
          fieldType = 'link';
          break;
        case 'src':
          fieldType = 'image';
          break;
        case 'data-redirect':
        case 'placeholder':
        case 'data-spm-text':
        case 'data-rcs-url-description':
          fieldType = attribute;
          break;
        default:
          break;
      }

      let callback = (value: string) => value;
      let translationValue = valueOfTranslation;
      if (valueOfTranslation.match(/^base64:/)) {
        callback = (value: string) => `base64:${btoa(value)}`;
        translationValue = atob(valueOfTranslation.replaceAll(/base64:/g, ''));
      }
      if (state.template?.type === 'sms') {
        callback = (value: string) => nl2br(value, true);
        translationValue = br2nl(valueOfTranslation);
      }

      let sectionId = 0;
      const section = getParents(elementWithTranslation, `.${TEMPLATE_SECTION_CLASS}`);

      if (section && section.length > 0) {
        sectionId = section[0].getAttribute('data-spmelementid');
      }

      if (sectionId > 0) {
        // Create translations for each language of current shop
        const translationsToAdd: Translation[] = UserState.activeShop?.langs.map((language: Lang) => ({
          section: sectionId ?? 0,
          language: language.id,
          fieldType,
          key,
          value: translationValue,
          callback,
        })) ?? [];

        // Merge with translations to add
        translationToAddToState = [...translationToAddToState, ...translationsToAdd];
      }
    }
  });

  return translationToAddToState.filter(
    (translation: any) => (state.template && !state.template.translations.find((stateTranslation) => stateTranslation.key === translation.key)) || (!state.template && true),
  );
};

export const translateDocument = (language: string, element: HTMLElement | null = null, templateState: any = null) => {
  let currentElement: HTMLElement;
  if (element === null) {
    const template = getTemplateIframeDocument();
    currentElement = template.body;
  } else {
    currentElement = element;
  }

  const missingTranslations = !templateState ? searchMissingTranslations(currentElement) : [];
  const translations = templateState ? templateState.translations : state.template?.translations;

  (translations ?? [])
    .filter((translation: Translation) => translation.language === language)
    .forEach((translation: Translation) => {
      if (isManagedByAttribute(translation)) { // insert attribute
        const attr = computeAttribute(translation.key, translation.fieldType);
        const items = currentElement.querySelectorAll(`[${attr}='{LANG_${translation.key}}']`);
        items.forEach((item) => {
          item.setAttribute(`${TRANSLATION_ATTRIBUTE}-${attr}`, `LANG_${translation.key}`);
          item.setAttribute(`${attr}`, translation.value);
        });
      } else { // insert markup
        // eslint-disable-next-line no-lonely-if
        if (currentElement.innerHTML.match(`{LANG_${translation.key}}`)) {
          currentElement.innerHTML = currentElement.innerHTML
            .replaceAll(`{LANG_${translation.key}}`,
              `<${TRANSLATION_MARKUP} id="LANG_${translation.key}">${translation.value}</${TRANSLATION_MARKUP}>`);
        }
      }
    });

  if (missingTranslations.length) {
    addTranslationsToState(missingTranslations);
  }

  return currentElement;
};

export const createTranslationIdentifier = (suffix = '') => {
  let identifier = Math.floor(Math.random() * Math.floor(Math.random() * Date.now())).toString(16)
    + Math.floor(Math.random() * Math.floor(Math.random() * Date.now())).toString(16);

  if (suffix) {
    identifier += `.${suffix}`;
  }

  return identifier;
};

export interface TranslationCreationResult {
  attribute?: string;
  markup?: string;
  id?: string;
  value: string;
}

export const createTranslation = (suffix = '', type = 'text', defaultValue = '', returnObject = false): TranslationCreationResult | string => {
  // generate unique key
  const identifier = createTranslationIdentifier(suffix);
  // create Translation object for each language
  const translations: Translation[] = UserState.activeShop?.langs.map((language: Lang) => ({
    section: state.template?.activeSection ?? 0,
    language: language.id,
    fieldType: type,
    key: identifier,
    value: defaultValue,
    callback: (value: string) => value,
  })) ?? [];

  addTranslationsToState(translations);
  if (isManagedByAttribute(translations[0])) { // return attribute
    const attr = computeAttribute(translations[0].key, translations[0].fieldType);
    return returnObject ? {
      attribute: `${TRANSLATION_ATTRIBUTE}-${attr}`,
      value: `LANG_${identifier}`,
    } : `${TRANSLATION_ATTRIBUTE}-${attr}="LANG_${identifier}"`;
  }
  // return markup
  return returnObject ? {
    markup: TRANSLATION_MARKUP,
    id: `LANG_${identifier}`,
    value: defaultValue,
  } : `<${TRANSLATION_MARKUP} id="LANG_${identifier}">${defaultValue}</${TRANSLATION_MARKUP}>`;
};

// Remove translation markup if exists as a direct child of the element, otherwise return the element itself
export const sanitizeTranslationMarkup = (element: HTMLElement): HTMLElement => {
  const children = element.querySelectorAll(`:scope ${TRANSLATION_MARKUP}`);

  if (children && children.length) {
    return children[0] as HTMLElement;
  }

  return element as HTMLElement;
};

/**
 * Decode HTML entities (e.g., &#39; → ') in text only, leaving tag markup untouched.
 * Decoding inside tags would turn encoded quotes in attributes (e.g.
 * style="font-family: &quot;comic sans ms&quot;") into raw quotes and break the markup.
 * @param html
 */
const decodeHtmlEntities = (html: string): string => html.replace(/<[^>]*>|[^<]+/g, (segment) => {
  if (segment.startsWith('<')) return segment;
  const textarea = document.createElement('textarea');
  textarea.innerHTML = segment;
  return textarea.value;
});

/**
 * Distinctive Lorem Ipsum words that don't exist in modern languages (FR, EN, ES, DE, IT, PT).
 * Used to detect placeholder text that should not be translated.
 */
const LOREM_IPSUM_WORDS = new Set([
  'lorem', 'ipsum', 'consectetur', 'adipiscing', 'eiusmod', 'incididunt',
  'nostrud', 'ullamco', 'aliquip', 'reprehenderit', 'cillum', 'occaecat',
  'cupidatat', 'proident', 'laborum', 'pellentesque', 'pharetra', 'phasellus',
  'vestibulum', 'volutpat', 'vulputate', 'hendrerit', 'fringilla', 'scelerisque',
  'tincidunt', 'sollicitudin', 'condimentum', 'eleifend', 'bibendum', 'fermentum',
  'suspendisse', 'maecenas', 'sagittis', 'tristique', 'facilisis', 'porttitor',
  'praesent', 'pulvinar', 'posuere', 'lobortis', 'curabitur', 'ultricies',
  'viverra', 'lacinia', 'laoreet', 'habitasse', 'egestas', 'dapibus',
]);

/**
 * Check if text is Lorem Ipsum placeholder content.
 * Returns true only when *every* meaningful word (>3 letters) belongs to the
 * distinctive Lorem Ipsum list — i.e. there is no user-written content mixed in.
 * Otherwise (e.g. "Livraison rapide. Lorem ipsum dolor..."), we still translate
 * so the user's real content gets translated, even if the lorem residue tags along.
 */
const isLoremIpsum = (text: string): boolean => {
  // Strip HTML tags and trim
  const stripped = text.replace(/<[^>]*>/g, '').trim().toLowerCase();
  if (!stripped) return false;

  // Extract words (alphabetic only)
  const words = stripped.match(/[a-z]+/g);
  if (!words) return false;

  // Ignore short connectors ("et", "in", "ut", ...) — too ambiguous to gate on.
  const meaningfulWords = words.filter((w) => w.length > 3);
  if (meaningfulWords.length === 0) return false;

  return meaningfulWords.every((word) => LOREM_IPSUM_WORDS.has(word));
};

/**
 * Translate a string from a language to another
 * @param translationValue
 * @param toLanguage
 */
export const translateFromGoogle = async (translationValue: string, toLanguage: string): Promise<string> => {
  const newLanguage = toLanguage === 'gb' ? 'en' : toLanguage;
  // Translate only if not empty and not just containing a variable
  if (translationValue && translationValue.trim() !== '' && !/^\{[A-Z_.]+}$/.test(translationValue)) {
    // Skip translation for Lorem Ipsum placeholder text
    if (isLoremIpsum(translationValue)) {
      return translationValue;
    }

    // Créer un map pour stocker les variables et leurs placeholders
    const variableMap = new Map<string, string>();
    let placeholderIndex = 0;

    // Utiliser des caractères mathématiques Unicode que Google ne traduit pas
    const protectedValue = translationValue.replace(/\{[^}]+\}/g, (match) => {
      const placeholder = `⟨VAR${placeholderIndex}⟩`;
      placeholderIndex += 1;
      variableMap.set(placeholder, match);
      return placeholder;
    });

    const data = {
      key: 'AIzaSyCCeaBJUffKPh1-Qak2xC-yd38f2TPCDOA',
      q: protectedValue,
      target: newLanguage,
      format: 'html',
    };

    const params = new URLSearchParams(data);

    const headers = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    };

    let result;
    try {
      result = await axios.get(`https://www.googleapis.com/language/translate/v2/?${params}`, { headers });
    } catch (e) {
      // Do nothing
    }

    if (result) {
      let translated = result.data.data.translations[0].translatedText;

      // Décoder les entités HTML (&#39; → ', &quot; → ", etc.)
      translated = decodeHtmlEntities(translated);

      // Restaurer les variables en remplaçant les placeholders
      variableMap.forEach((originalVariable, placeholder) => {
        translated = translated.replaceAll(placeholder, originalVariable);
      });

      return translated;
    }
  }

  return translationValue;
};

export const updateTranslation = (selector: string, property: Property) => {
  const template = getTemplateIframeDocument();
  if (!template?.body) return;
  const elements = template.body.querySelectorAll(selector);

  const translationKeys: Record<string, any> = {};

  Array.from(elements).forEach((element) => {
    if (element.childNodes) {
      const childNodes = Array.from(element.childNodes);
      const childNode = childNodes.find((item) => {
        const child = item as Element;
        if (child.attributes && 'data-spmtranslationid-placeholder' in child.attributes) {
          return true;
        }
        return false;
      });
      if (childNode) {
        const childNodeId = (childNode as Element).getAttribute('id');
        if (Array.isArray(property.value)) {
          const field = property.value.find((item: any) => item.key === childNodeId);
          const attribute = (childNode as Element).getAttribute('data-spmtranslationid-placeholder');
          if (attribute && field) {
            if (!translationKeys[attribute.replace('LANG_', '')]) {
              translationKeys[attribute.replace('LANG_', '')] = {};
            }
            translationKeys[attribute.replace('LANG_', '')].required = field.required;
            const attributeValue = (childNode as Element).getAttribute('placeholder');
            if (attributeValue) {
              translationKeys[attribute.replace('LANG_', '')].value = attributeValue;
            }
          }
        }
      }
    }
  });

  Object.keys(translationKeys).forEach((translationKey) => {
    if (state.template) {
      const translations = state.template.translations.filter((item) => item.key === translationKey);
      translations.forEach((translation) => {
        const isRequired = translationKeys[translationKey].required;
        let translationValue = translationKeys[translationKey].value || translation.value;
        if (isRequired && translationValue.charAt(translationValue.length - 1) !== '*') {
          translationValue = `${translationValue}*`;
        } else if (!isRequired && translationValue.charAt(translationValue.length - 1) === '*') {
          translationValue = translationValue.slice(0, -1);
        }
        replaceTranslationInState(translationKey, translation.language, translationValue);
      });
    }
  });
};

function insertAttributeTranslation(currentElement: cheerio.Cheerio<cheerio.Node>, translation: Translation) {
  const attr = computeAttribute(translation.key, translation.fieldType);
  const item = currentElement.find(`[${attr}='{LANG_${translation.key}}']`);
  if (item.length > 0) {
    item.attr(`${TRANSLATION_ATTRIBUTE}-${attr}`, `LANG_${translation.key}`);
    item.attr(`${attr}`, translation.value);
  }
}

function insertMarkupTranslation(currentElement: cheerio.Cheerio<cheerio.Node>, translation: Translation) {
  const langKeyRegex = new RegExp(`{LANG_${translation.key}}`, 'g');
  const currentHTML = currentElement.html() || '';
  if (currentHTML.match(langKeyRegex)) {
    const markup = `<${TRANSLATION_MARKUP} id="LANG_${translation.key}">${translation.value}</${TRANSLATION_MARKUP}>`;
    const newHTML = currentHTML.replace(langKeyRegex, markup);
    currentElement.html(newHTML);
  }
}

export const translateElement = (element: cheerio.Cheerio<cheerio.Node>, translations: Translation[], defaultLanguage: string) => {
  translations.filter((translation: Translation) => translation.language === defaultLanguage)
    .forEach((translation: Translation) => {
      if (isManagedByAttribute(translation)) {
        insertAttributeTranslation(element, translation);
      } else {
        insertMarkupTranslation(element, translation);
      }
    });
};

interface ProcessOptions {
  ignoreTable?: boolean;
  onlyContent?: boolean;
  onlyAttributes?: boolean;
}

interface ElementAttribute {
  name: string;
  type: string;
  data: Function;
}

// Liste des attributs traduisibles - utilisée par generateTranslations et setAttribute
export const TRANSLATABLE_ATTRIBUTES = ['src', 'alt', 'placeholder', 'href', 'title', 'srcset', 'data-selectoptionlabel', 'data-selectoptionplaceholder', 'data-spm-text'];

const elementAttributes: ElementAttribute[] = [
  {
    name: 'src',
    type: 'image',
    data: (value: any) => ({
      src: {
        type: 'image',
        value,
      },
    }),
  },
  {
    name: 'alt',
    type: 'text',
    data: (value: any) => ({
      alt: {
        type: 'text',
        value,
      },
    }),
  },
  {
    name: 'placeholder',
    type: 'placeholder',
    data: (value: any) => (
      {
        type: 'placeholder',
        value,
      }
    ),
  },
  {
    name: 'href',
    type: 'link',
    data: (value: any) => ({
      href: {
        type: 'link',
        value,
      },
    }),
  },
  {
    name: 'title',
    type: 'text',
    data: (value: any) => ({
      title: {
        type: 'text',
        value,
      },
    }),
  },
  {
    name: 'srcset',
    type: 'image',
    data: (value: any) => ({
      srcset: {
        type: 'image',
        value,
      },
    }),
  },
  {
    name: 'data-selectoptionlabel',
    type: 'data-selectoptionlabel',
    data: (value: any) => (
      {
        type: 'data-selectoptionlabel',
        value,
      }
    ),
  },
  {
    name: 'data-selectoptionplaceholder',
    type: 'data-selectoptionplaceholder',
    data: (value: any) => (
      {
        type: 'data-selectoptionplaceholder',
        value,
      }
    ),
  },
  {
    name: 'data-spm-text',
    type: 'data-spm-text',
    data: (value: any) => (
      {
        'data-spm-text': {
          type: 'data-spm-text',
          value,
        },
      }
    ),
  },
];

interface GenerateTranslationsOptions {
  newElement?: boolean;
  fullHtml?: boolean;
  translateVariables?: boolean;
  globalTranslate?: boolean;
  ignoreSpecificItems?: boolean;
  checkDiv?: boolean;
  forceTranslateHiddenElement?: boolean;
}

export const generateTranslations = (
  html: string,
  {
    newElement = false,
    fullHtml = false,
    translateVariables = true,
    globalTranslate = false,
    ignoreSpecificItems = false,
    checkDiv = true,
    forceTranslateHiddenElement = false,
  }: GenerateTranslationsOptions = {},
) => {
  const $ = cheerio.load(html);

  let langs = ['fr'];
  if (UserState.activeShop && Array.isArray(UserState.activeShop.langs) && UserState.activeShop.langs.length) {
    langs = UserState.activeShop.langs.map((lang) => lang.id);
  }

  let translations: any[] = [];
  const sectionIds: Record<string, any> = {};

  const addSectionId = (htmlElement: cheerio.Cheerio<cheerio.Node>, key: string) => {
    const section = htmlElement.closest('[data-spmelementid]');
    if (section) {
      const sectionId = section.attr('data-spmelementid');
      if (sectionId) {
        sectionIds[key] = parseInt(sectionId, 10);
      }
    }
  };

  const addToTranslations = (key: string, normalizedKey: string, fieldType: string, fieldValue: any, value: any) => {
    translations.push({
      key,
      normalizedKey,
      fieldType,
      fieldValue,
      value,
    });
  };

  const checkVariableRegex = /^\{var=[^}]+\}$/g;

  const processElement = (
    element: cheerio.Node,
    {
      ignoreTable = false,
      onlyContent = false,
      onlyAttributes = false,
    }: ProcessOptions = {},
  ) => {
    const htmlElement = $(element);

    const elementType = (element as unknown as cheerio.Element).tagName?.toLowerCase();

    // Skip elements with spm_no_translate class and their children
    if (htmlElement.hasClass('spm_no_translate') || htmlElement.closest('.spm_no_translate').length > 0) {
      return;
    }

    if (isEditorOnlyElement(htmlElement)) {
      return;
    }

    // Skip SVG elements and their children — SVG text cannot contain spmtranslation markup
    if (elementType === 'svg' || htmlElement.closest('svg').length > 0) {
      return;
    }

    if (!TRANSLATION_EXCLUDE_IDS.includes(htmlElement.attr('id') || '')) {
      // This condition prevent translated element to be translated twice
      if (elementType !== TRANSLATION_MARKUP) {
        if (globalTranslate) {
          const key = createTranslationIdentifier();
          addToTranslations(
            key,
            `${key}`,
            'html',
            htmlElement.html(),
            {
              type: 'html',
              value: htmlElement.html(),
            },
          );
          htmlElement.html(`{LANG_${key}}`);
          addSectionId(htmlElement, key);
        } else {
          // eslint-disable-next-line no-lonely-if
          if (element.nodeType === Node.TEXT_NODE && htmlElement.text().replace(/&nbsp;/g, ' ').trim()) {
            const doTextTranslation = () => {
              const key = createTranslationIdentifier();
              addToTranslations(
                key,
                `${key}.text`,
                'text',
                htmlElement.text(),
                {
                  text: {
                    type: 'text',
                    value: htmlElement.text(),
                  },
                },
              );
              addSectionId(htmlElement, `${key}.text`);
              htmlElement.replaceWith(`{LANG_${key}.text}`);
            };
            if (translateVariables) {
              doTextTranslation();
            } else if (!translateVariables && !htmlElement.text().trim().match(checkVariableRegex)) {
              doTextTranslation();
            }
          } else if (element.nodeType === Node.ELEMENT_NODE && ((!forceTranslateHiddenElement && !htmlElement.hasClass('spm_hidden')) || forceTranslateHiddenElement)) {
            // Translate attributes
            const translateAttributes = () => {
              // eslint-disable-next-line no-restricted-syntax
              for (const elementAttribute of elementAttributes) {
                const attr = htmlElement.attr(elementAttribute.name);
                if (attr !== undefined && htmlElement.attr(`${TRANSLATION_ATTRIBUTE}-${elementAttribute.name}`) === undefined && !attr.match(/{LANG_([a-z0-9]+)\.?\w*}/g)) {
                  const doAttributeTranslation = () => {
                    const key = createTranslationIdentifier();
                    const attrType = elementAttribute.type;
                    addToTranslations(
                      key,
                      elementAttribute.name !== 'data-redirect' ? `${key}.${elementAttribute.name}` : key,
                      attrType,
                      htmlElement.attr(elementAttribute.name),
                      elementAttribute.data(htmlElement.attr(elementAttribute.name)),
                    );
                    htmlElement.attr(elementAttribute.name, `{LANG_${key}.${elementAttribute.name}}`);
                    addSectionId(htmlElement, elementAttribute.name !== 'data-redirect' ? `${key}.${elementAttribute.name}` : key);
                  };
                  if (translateVariables) {
                    doAttributeTranslation();
                  } else if (!translateVariables && !htmlElement.attr(elementAttribute.name)?.match(checkVariableRegex)) {
                    doAttributeTranslation();
                  }
                }
              }
            };

            // Translate contents
            const translateContent = () => {
              if (htmlElement.hasClass(TEMPLATE_TRANSLATE_SPECIFIC_HTML_CONTENT)) {
                const key = createTranslationIdentifier();
                addToTranslations(
                  key,
                  `${key}`,
                  'html',
                  htmlElement.html(),
                  {
                    type: 'html',
                    value: htmlElement.html(),
                  },
                );
                htmlElement.html(`{LANG_${key}}`);
                addSectionId(htmlElement, key);
              } else {
                htmlElement.contents().each((index, child) => {
                  const childElement = $(child);
                  if (child.nodeType === Node.TEXT_NODE && childElement.text().replace(/&nbsp;/g, ' ').trim()) {
                    processElement(child, { ignoreTable, onlyContent, onlyAttributes });
                  } else if (child.nodeType === Node.ELEMENT_NODE) {
                    processElement(child, { ignoreTable, onlyContent, onlyAttributes });
                  }
                });
              }
            };

            const handleElementTranslationTypeHtml = (htmlElementToHandle: cheerio.Cheerio<cheerio.Node>, ignoreTableTag = false, checkOnlyText = false) => {
              let childrenCount = 0;
              let textCount = 0;

              htmlElementToHandle.contents().each((index, child) => {
                const childElement = $(child);
                const childType = (child as unknown as cheerio.Element).tagName?.toLowerCase();

                if (childType !== 'br' && childType !== 'table' && childType !== 'tbody') {
                  if (child.nodeType === Node.TEXT_NODE && childElement.text().replace(/&nbsp;/g, ' ').trim()) {
                    childrenCount += 1;
                    textCount += 1;
                  } else if (child.nodeType === Node.ELEMENT_NODE && !checkOnlyText && !isEditorOnlyElement(childElement)) {
                    childrenCount += 1;
                    if (['a', 'label', 'span', 'p', 'strong'].includes(childType) || childType.match(/^h[1-6]$/)) {
                      textCount += 1;
                    }
                  }
                }
              });

              if (childrenCount === 1 || (childrenCount > 1 && textCount === 0)) {
                htmlElementToHandle.contents().each((index, child) => {
                  const childElement = $(child);
                  if (child.nodeType === Node.TEXT_NODE && childElement.text().replace(/&nbsp;/g, ' ').trim()) {
                    processElement(child, { ignoreTable: ignoreTableTag, onlyAttributes, onlyContent });
                  } else if (child.nodeType === Node.ELEMENT_NODE) {
                    processElement(child, { ignoreTable: ignoreTableTag, onlyAttributes, onlyContent });
                  }
                });
              } else if (textCount > 0) {
                if (!containsTranslatedContent(htmlElementToHandle.html())) {
                  const key = createTranslationIdentifier();
                  addToTranslations(
                    key,
                    `${key}`,
                    'html',
                    htmlElementToHandle.html(),
                    {
                      type: 'html',
                      value: htmlElementToHandle.html(),
                    },
                  );
                  htmlElementToHandle.html(`{LANG_${key}}`);
                  addSectionId(htmlElement, key);
                } else {
                  // Already translated content: translate remaining text nodes individually
                  htmlElementToHandle.contents().each((index, child) => {
                    const childElement = $(child);
                    if (child.nodeType === Node.TEXT_NODE && childElement.text().replace(/&nbsp;/g, ' ').trim()) {
                      processElement(child, { ignoreTable: ignoreTableTag, onlyAttributes, onlyContent });
                    } else if (child.nodeType === Node.ELEMENT_NODE) {
                      processElement(child, { ignoreTable: ignoreTableTag, onlyAttributes, onlyContent });
                    }
                  });
                }
              }
            };

            if (elementType === 'p' || elementType === 'blockquote') {
              // Prevent element to be translated twice
              if (!htmlElement.html()?.match(RegExp(`<${TRANSLATION_MARKUP}\\b[^>]*>`, 'gi'))) {
                const doHtmlTranslation = () => {
                  const key = createTranslationIdentifier();
                  addToTranslations(
                    key,
                    `${key}`,
                    'html',
                    htmlElement.html(),
                    {
                      type: 'html',
                      value: htmlElement.html(),
                    },
                  );
                  htmlElement.html(`{LANG_${key}}`);
                  addSectionId(htmlElement, key);
                };

                // Check if no text child
                let hasText = false;
                htmlElement.contents().each((indexChild, child) => {
                  const childElement = $(child);
                  if (child.nodeType === Node.TEXT_NODE && childElement.text().replace(/&nbsp;/g, ' ').trim()) {
                    hasText = true;
                  }
                });

                if (!hasText) {
                  htmlElement.contents().each((indexChild, child) => {
                    processElement(child);
                  });
                } else {
                  // eslint-disable-next-line no-lonely-if
                  if (translateVariables) {
                    doHtmlTranslation();
                  } else if (!translateVariables && !htmlElement.text().trim().match(checkVariableRegex)) {
                    doHtmlTranslation();
                  }
                }
              }
            } else if (elementType === 'div' && !htmlElement.hasClass('fb_profile_picture')) { // Ignore fb profile picture
              if (checkDiv && htmlElement.hasClass('spm_rich_editor') && !htmlElement.html()?.match(RegExp(`<${TRANSLATION_MARKUP}\\b[^>]*>`, 'gi'))) {
                const key = createTranslationIdentifier();
                addToTranslations(
                  key,
                  `${key}`,
                  'html',
                  htmlElement.html(),
                  {
                    type: 'html',
                    value: htmlElement.html(),
                  },
                );
                htmlElement.html(`{LANG_${key}}`);
                addSectionId(htmlElement, key);
              } else {
                // eslint-disable-next-line no-lonely-if
                if (!checkDiv) {
                  handleElementTranslationTypeHtml(htmlElement, false, true);
                } else {
                  // eslint-disable-next-line no-lonely-if
                  if (!onlyContent && !onlyAttributes) {
                    translateAttributes();
                    translateContent();
                  } else if (onlyContent) {
                    translateContent();
                  } else if (onlyAttributes) {
                    translateAttributes();
                  }
                }
              }
            } else if (elementType === 'table' && !ignoreTable) {
              const thElements = htmlElement.find('th');
              const tdElements = htmlElement.find('td');
              const captionElements = htmlElement.find('caption');
              captionElements.each((index, captionElement) => {
                handleElementTranslationTypeHtml($(captionElement));
              });

              thElements.each((index, thElement) => {
                const $th = $(thElement);
                // Skip the product loop template element itself (has s-collection attribute) —
                // its direct children (h4, p) contain only product variables, not translatable text.
                // Nested <th> descendants (button, price) are still processed normally by find('th').
                if ($th.attr('s-collection')) return;
                handleElementTranslationTypeHtml($th, true);
              });

              tdElements.each((index, tdElement) => {
                const $td = $(tdElement);
                if ($td.attr('s-collection')) return;
                handleElementTranslationTypeHtml($td, true);
              });
            } else if (elementType === 'li') {
              handleElementTranslationTypeHtml(htmlElement);
            } else if (elementType === 'ul' || elementType === 'ol') {
              const liElements = htmlElement.find('li');
              liElements.each((index, liElement) => {
                handleElementTranslationTypeHtml($(liElement));
              });
            } else if (elementType === 'button' || elementType === 'label') {
              // Prevent element to be translated twice
              if (!htmlElement.html()?.match(RegExp(`<${TRANSLATION_MARKUP}\\b[^>]*>`, 'gi'))) {
                const doTranslation = () => {
                  const key = createTranslationIdentifier();
                  addToTranslations(
                    key,
                    `${key}.text`,
                    'text',
                    htmlElement.text(),
                    {
                      text: {
                        type: 'text',
                        value: htmlElement.text(),
                      },
                    },
                  );
                  htmlElement.text(`{LANG_${key}.text}`);
                  addSectionId(htmlElement, `${key}.text`);
                };
                if (translateVariables) {
                  doTranslation();
                } else if (!translateVariables
                    && htmlElement.text().replace(/&nbsp;/g, ' ').trim()
                    && !htmlElement.text().replace(/&nbsp;/g, ' ').trim().match(checkVariableRegex)) {
                  doTranslation();
                }
              }
            } else if (elementType.match(/^h[1-6]$/) || elementType === 'span') {
              let onlyText = true;
              let childCount = 0;
              let textCount = 0;
              htmlElement.contents().each((index, child) => {
                const childElement = $(child);
                const childType = (child as unknown as cheerio.Element).tagName?.toLowerCase();

                if (child.nodeType === Node.TEXT_NODE && childElement.text().replace(/&nbsp;/g, ' ').trim()) {
                  textCount += 1;
                  childCount += 1;
                } else if (child.nodeType === Node.ELEMENT_NODE) {
                  if (childType !== 'strong') {
                    onlyText = false;
                  }
                  childCount += 1;
                }
              });
              if (onlyText && childCount === 1 && textCount > 0 && !htmlElement.text().trim().match(checkVariableRegex)) {
                const key = createTranslationIdentifier();
                addToTranslations(
                  key,
                  `${key}.text`,
                  'text',
                  htmlElement.text(),
                  {
                    text: {
                      type: 'text',
                      value: htmlElement.text(),
                    },
                  },
                );
                htmlElement.text(`{LANG_${key}.text}`);
                addSectionId(htmlElement, `${key}.text`);
              } else if (onlyText && childCount > 1) {
                const key = createTranslationIdentifier();
                addToTranslations(
                  key,
                  `${key}`,
                  'html',
                  htmlElement.html(),
                  {
                    type: 'html',
                    value: htmlElement.html(),
                  },
                );
                htmlElement.html(`{LANG_${key}}`);
                addSectionId(htmlElement, key);
              } else {
                htmlElement.contents().each((index, child) => {
                  const childElement = $(child);
                  if (child.nodeType === Node.TEXT_NODE && childElement.text().replace(/&nbsp;/g, ' ').trim()) {
                    processElement(child, { ignoreTable, onlyAttributes, onlyContent });
                  } else if (child.nodeType === Node.ELEMENT_NODE) {
                    processElement(child, { ignoreTable, onlyAttributes, onlyContent });
                  }
                });
              }
            } else if (elementType === 'option') {
              translateAttributes();
            } else if (elementType !== 'tbody') { // We exclude the tbody tag because it's already taken into account in table verification
              // eslint-disable-next-line no-lonely-if
              if (!onlyContent && !onlyAttributes) {
                translateAttributes();
                translateContent();
              } else if (onlyContent) {
                translateContent();
              } else if (onlyAttributes) {
                translateAttributes();
              }
            }
          }
        }
      }
    }
  };

  if (globalTranslate) {
    processElement($('body').get(0));
  } else {
    // eslint-disable-next-line no-lonely-if
    if (!ignoreSpecificItems && ($('body').find(`.${TEMPLATE_TRANSLATE_SPECIFIC}`).length
      || $('body').find(`.${TEMPLATE_TRANSLATE_SPECIFIC_ITEMS}`).length
      || $('body').find(`.${TEMPLATE_TRANSLATE_SPECIFIC_ONLY_CONTENT}`).length
      || $('body').find(`.${TEMPLATE_TRANSLATE_SPECIFIC_ONLY_ATTRIBUTE}`).length
    )) {
      const specificItemsElements = $('body').find(`.${TEMPLATE_TRANSLATE_SPECIFIC_ITEMS}`);
      specificItemsElements.each((index, element) => {
        processElement(element);
      });

      const specificItemsOnlyContentElements = $('body').find(`.${TEMPLATE_TRANSLATE_SPECIFIC_ONLY_CONTENT}`);
      specificItemsOnlyContentElements.each((index, element) => {
        processElement(element, { onlyContent: true });
      });

      const specificItemsOnlyAttributesElements = $('body').find(`.${TEMPLATE_TRANSLATE_SPECIFIC_ONLY_ATTRIBUTE}`);
      specificItemsOnlyAttributesElements.each((index, element) => {
        processElement(element, { onlyAttributes: true });
      });
    } else {
      $('body').contents().each((index, element) => {
        processElement(element);
      });
    }
  }

  // Generate translations for game wheel prize labels (SVG text is skipped by processElement)
  $('body').find('.spm_widget_game_wheel').each((_, widgetEl) => {
    const widget = $(widgetEl);
    const prizesStr = widget.attr('data-prizes');
    if (prizesStr) {
      try {
        const prizes = JSON.parse(prizesStr);
        let modified = false;
        prizes.forEach((prize: any) => {
          if (prize.label && !prize.translationKey) {
            const key = createTranslationIdentifier();
            const normalizedKey = `${key}.data-spm-text`;
            prize.translationKey = normalizedKey;
            modified = true;
            addToTranslations(
              key,
              normalizedKey,
              'data-spm-text',
              prize.label,
              {
                'data-spm-text': {
                  type: 'data-spm-text',
                  value: prize.label,
                },
              },
            );
            addSectionId(widget, normalizedKey);
          }
        });
        if (modified) {
          widget.attr('data-prizes', JSON.stringify(prizes));
        }
      } catch (e) {
        // data-prizes is not valid JSON, skip
      }
    }
  });

  // Generate translations for slot machine symbol labels and prize names (reel content is spm_no_translate)
  const slotWidgets = $('body').find('.spm_widget_game_slot_machine');
  slotWidgets.each((_, widgetEl) => {
    const widget = $(widgetEl);

    // Translate symbol labels (text displayed on reels)
    const symbolsStr = widget.attr('data-symbols');
    if (symbolsStr) {
      try {
        const symbols = JSON.parse(symbolsStr);
        let modified = false;
        symbols.forEach((symbol: any) => {
          if (symbol.type === 'text' && symbol.text && !symbol.translationKey) {
            const key = createTranslationIdentifier();
            const normalizedKey = `${key}.data-spm-slot-symbol`;
            symbol.translationKey = normalizedKey;
            modified = true;
            addToTranslations(
              key,
              normalizedKey,
              'data-spm-slot-symbol',
              symbol.text,
              {
                'data-spm-slot-symbol': {
                  type: 'data-spm-slot-symbol',
                  value: symbol.text,
                },
              },
            );
            addSectionId(widget, normalizedKey);
          }
        });
        if (modified) {
          widget.attr('data-symbols', JSON.stringify(symbols));
        }
      } catch (e) {
        // data-symbols is not valid JSON, skip
      }
    }

    // Translate win combination prize names
    const winCombStr = widget.attr('data-win-combinations');
    if (winCombStr) {
      try {
        const winCombinations = JSON.parse(winCombStr);
        let modified = false;
        winCombinations.forEach((combo: any) => {
          if (combo.prize && !combo.translationKey) {
            const key = createTranslationIdentifier();
            const normalizedKey = `${key}.data-spm-slot-prize`;
            combo.translationKey = normalizedKey;
            modified = true;
            addToTranslations(
              key,
              normalizedKey,
              'data-spm-slot-prize',
              combo.prize,
              {
                'data-spm-slot-prize': {
                  type: 'data-spm-slot-prize',
                  value: combo.prize,
                },
              },
            );
            addSectionId(widget, normalizedKey);
          }
        });
        if (modified) {
          widget.attr('data-win-combinations', JSON.stringify(winCombinations));
        }
      } catch (e) {
        // data-win-combinations is not valid JSON, skip
      }
    }
  });

  // Generate translations for scratch card prize labels (same data-prizes format as wheel)
  $('body').find('.spm_widget_game_scratch_card').each((_, widgetEl) => {
    const widget = $(widgetEl);
    const prizesStr = widget.attr('data-prizes');
    if (prizesStr) {
      try {
        const prizes = JSON.parse(prizesStr);
        let modified = false;
        prizes.forEach((prize: any) => {
          if (prize.label && !prize.translationKey) {
            const key = createTranslationIdentifier();
            const normalizedKey = `${key}.data-spm-text`;
            prize.translationKey = normalizedKey;
            modified = true;
            addToTranslations(
              key,
              normalizedKey,
              'data-spm-text',
              prize.label,
              {
                'data-spm-text': {
                  type: 'data-spm-text',
                  value: prize.label,
                },
              },
            );
            addSectionId(widget, normalizedKey);
          }
        });
        if (modified) {
          widget.attr('data-prizes', JSON.stringify(prizes));
        }
      } catch (e) {
        // data-prizes is not valid JSON, skip
      }
    }
  });

  const translationsObject: Record<string, any> = {};

  langs.forEach((lang) => {
    translationsObject[lang] = {};
  });

  // Sort the translations array to match the position in the html
  const matches = $.html().match(/{LANG_([a-z0-9]+)\.?\w*}/g);
  if (matches && matches.length) {
    const extractedKeys = matches.map((str) => {
      const match = str.match(/{LANG_([a-zA-Z0-9]+)\b/);
      return match ? match[1] : null;
    }).filter(Boolean) as string[];

    translations = translations.sort((a, b) => extractedKeys.indexOf(a.key) - extractedKeys.indexOf(b.key));
  }

  const translationsArray: Translation[] = [];

  translations.forEach((translation) => {
    Object.keys(translationsObject).forEach((translationKey) => {
      translationsObject[translationKey][translation.key] = translation.value;
    });
    langs.forEach((lang) => {
      let sectionId = sectionIds[translation.normalizedKey];
      if (!sectionId) {
        // Try to find the correct section by checking which section's HTML contains this LANG key
        const baseKey = translation.normalizedKey.split('.')[0];
        const correctSection = state.template?.sections.find(
          (s) => s.type !== 'design' && new RegExp(`LANG_${baseKey}`).test(s.html),
        );
        sectionId = correctSection
          ? Number(correctSection.id_template_elements)
          : state.template?.activeSection;
      }
      translationsArray.push({
        section: sectionId,
        language: lang,
        fieldType: translation.fieldType,
        key: translation.normalizedKey,
        value: translation.fieldValue,
        callback: (value: string) => value,
      });
    });
  });

  if (newElement || globalTranslate) {
    translateElement($('body'), translationsArray, getShopDefaultLang());
    addTranslationsToState(translationsArray);
  }

  if (globalTranslate) {
    return {
      newHtml: $('body').html() as string,
      translations: translationsObject,
      translationsArray,
    };
  }

  return {
    newHtml: !newElement || fullHtml ? $.html() as string : $('body').html() as string,
    translations: translationsObject,
    translationsArray,
  };
};

export const removeTranslationInState = (key: string) => {
  removeTranslation(key);
};

export const setTranslationInState = (element: HTMLElement, type: string, attributeName = '', replaceOuterHtml = false) => {
  // get translation key from element
  let key;
  let value;
  if (type === 'markup') {
    const translationElement = element.querySelector(TRANSLATION_MARKUP);
    key = translationElement?.getAttribute('id')?.replace('LANG_', '');
    value = translationElement?.innerHTML;
  } else {
    const translationAttribute = element?.getAttribute(`${TRANSLATION_ATTRIBUTE}${attributeName ? `-${attributeName}` : ''}`);

    if (!translationAttribute) {
      // Element doesn't have the translation attribute (element has just been created with attribute `attributeName` (e.g. new link on element)),
      // we create the translation attribute
      const translationIdentifier = createTranslationIdentifier();
      element.setAttribute(`${TRANSLATION_ATTRIBUTE}${attributeName ? `-${attributeName}` : ''}`, `LANG_${translationIdentifier}`);

      // Create Translation object for each language
      const translations: Translation[] = UserState.activeShop?.langs.map((language: Lang) => ({
        section: state.template?.activeSection ?? 0,
        language: language.id,
        fieldType: type,
        key: translationIdentifier,
        value: '',
        callback: (translationValue: string) => translationValue,
      })) ?? [];

      // Add translations to state
      addTranslationsToState(translations);
    }

    // Get translation key (created above or already existing on element) and get value of attribute
    key = element?.getAttribute(`${TRANSLATION_ATTRIBUTE}${attributeName ? `-${attributeName}` : ''}`)?.replace('LANG_', '');
    value = element?.getAttribute(attributeName);
  }
  if (key) {
    if (attributeName === 'href') {
      // In case of a link, we replace the link (or the redirection) in every language
      replaceTranslationInStateAllLanguages(key, value ?? '');
    } else {
      const defaultLanguage: string = UserState.activeShop?.langs.filter((lang) => lang.default).map((lang) => lang.id)[0] ?? 'fr';
      replaceTranslationInState(key, defaultLanguage, value ?? '');
    }
  } else {
    const { newHtml } = generateTranslations(element.outerHTML, { newElement: true });
    if (!replaceOuterHtml) {
      // eslint-disable-next-line no-param-reassign
      element.innerHTML = newHtml;
    } else {
      // eslint-disable-next-line no-param-reassign
      element.outerHTML = newHtml;
    }
  }
};

export const fixMissingTranslations = () => {
  const template = getTemplateIframeDocument();
  if (template) {
    const spmTemplate = template.body.querySelector('spm-template');
    if (spmTemplate) {
      // eslint-disable-next-line no-param-reassign
      spmTemplate.innerHTML = spmTemplate.innerHTML.replace(/{LANG_([a-z0-9]+)\.?\w*}/g, '');
      const { newHtml, translationsArray } = generateTranslations(spmTemplate.innerHTML, { newElement: true, ignoreSpecificItems: true, translateVariables: false });
      // eslint-disable-next-line no-param-reassign
      spmTemplate.innerHTML = newHtml;

      // Add newly generated translations to state (e.g. game widget prizes stored in data-prizes JSON)
      if (translationsArray.length) {
        addTranslationsToState(translationsArray);
      }

      // Recover game widget translations that have translationKey in DOM but no entries in state
      // (can happen if cleanUpTranslations previously removed them incorrectly)
      const gameWidgetSelectors = [
        { selector: '.spm_widget_game_wheel', attr: 'data-prizes', fieldType: 'data-spm-text', labelKey: 'label' },
        { selector: '.spm_widget_game_scratch_card', attr: 'data-prizes', fieldType: 'data-spm-text', labelKey: 'label' },
        { selector: '.spm_widget_game_slot_machine', attr: 'data-symbols', fieldType: 'data-spm-slot-symbol', labelKey: 'text' },
        { selector: '.spm_widget_game_slot_machine', attr: 'data-win-combinations', fieldType: 'data-spm-slot-prize', labelKey: 'prize' },
      ];
      const defaultLang = getShopDefaultLang();
      const recoveredTranslations: Translation[] = [];
      gameWidgetSelectors.forEach(({ selector, attr, fieldType, labelKey }) => {
        spmTemplate.querySelectorAll(selector).forEach((widget: Element) => {
          const jsonStr = widget.getAttribute(attr);
          if (!jsonStr) return;
          try {
            const items = JSON.parse(jsonStr);
            items.forEach((item: any) => {
              if (item.translationKey && item[labelKey]) {
                // Check if this translationKey already has entries in state
                const existsInState = state.template?.translations.some(
                  (t) => t.key === item.translationKey && t.language === defaultLang,
                );
                if (!existsInState) {
                  // Find the section ID for this widget
                  const sectionEl = widget.closest('[data-spmelementid]');
                  const sectionId = sectionEl ? parseInt(sectionEl.getAttribute('data-spmelementid') || '0', 10) : (state.template?.activeSection ?? 0);
                  // Create translation entries for all languages
                  UserState.activeShop?.langs.forEach((language: Lang) => {
                    recoveredTranslations.push({
                      section: sectionId,
                      language: language.id,
                      fieldType,
                      key: item.translationKey,
                      value: item[labelKey],
                      callback: (value: string) => value,
                    });
                  });
                }
              }
            });
          } catch (e) { /* invalid JSON, skip */ }
        });
      });
      if (recoveredTranslations.length) {
        addTranslationsToState(recoveredTranslations);
      }

      // Remove excluded ids from translations
      TRANSLATION_EXCLUDE_IDS.forEach((excludedId) => {
        const elements = spmTemplate.querySelectorAll(`#${excludedId}`);
        Array.from(elements).forEach((element) => {
          if (element.innerHTML.match(RegExp(`<${TRANSLATION_MARKUP}\\b[^>]*>`, 'gi'))) {
            const key = element.children[0].getAttribute('id');
            if (key) {
              removeTranslationInState(key.replace('LANG_', ''));
            }
            // eslint-disable-next-line no-param-reassign
            element.innerHTML = element.children[0].innerHTML;
          }
        });
      });

      // Remove translation from data-redirect
      const elements = spmTemplate.querySelectorAll('div[data-redirect]');
      Array.from(elements).forEach((element) => {
        if (!isWhatsappTemplate(state.template?.type)) {
          if (element.children.length === 1 && element.innerHTML.match(RegExp(`<${TRANSLATION_MARKUP}\\b[^>]*>`, 'gi'))) {
            const key = element.children[0].getAttribute('id');
            if (key) {
              removeTranslationInState(key.replace('LANG_', ''));
            }
            // eslint-disable-next-line no-param-reassign
            element.innerHTML = element.children[0].innerHTML;
          }
          if (!element.getAttribute('data-redirect') && element.getAttribute(`${TRANSLATION_ATTRIBUTE}-data-redirect`)) {
            const key = element.getAttribute(`${TRANSLATION_ATTRIBUTE}-data-redirect`);
            if (key) {
              removeTranslationInState(key.replace('LANG_', ''));
              element.removeAttribute(`${TRANSLATION_ATTRIBUTE}-data-redirect`);
            }
          }
          // Remove translation if element data-redirect has spmaction executeJS
          if (element.getAttribute('data-redirect') && element.classList.contains(`spm_action_${ActionTypeEnum.EXECUTE_JS}`)) {
            const key = element.getAttribute(`${TRANSLATION_ATTRIBUTE}-data-redirect`);
            if (key) {
              removeTranslationInState(key.replace('LANG_', ''));
              element.removeAttribute(`${TRANSLATION_ATTRIBUTE}-data-redirect`);
            }
          }
        }
      });

      // Remove translation from fb profile picture
      const profilePictureElements = spmTemplate.querySelectorAll('div.fb_profile_picture');
      Array.from(profilePictureElements).forEach((element) => {
        if (element.children.length === 1 && element.children[0].tagName.toLowerCase() === 'img') {
          const attributeName = `${TRANSLATION_ATTRIBUTE}-src`;
          const attributeKey = element.children[0].getAttribute(attributeName);
          if (attributeKey) {
            removeTranslationInState(attributeKey.replace('LANG_', ''));
            element.children[0].removeAttribute(`${TRANSLATION_ATTRIBUTE}-src`);
          }
        }
      });

      // Fix missing translations for different languages
      fixMissingTranslationsForShopLanguages();

      cleanUpTranslations();
    }
  }
};

export const fixDataRedirect = (template: Document) => {
  // Fix retroactive: restore data-redirect values from translations and remove translation entries
  const defaultLanguage: string = getShopDefaultLang();
  const dataRedirectElements = template.querySelectorAll('[data-redirect^="{LANG_"]');
  Array.from(dataRedirectElements).forEach((element) => {
    const dataRedirectValue = element.getAttribute('data-redirect');
    if (dataRedirectValue) {
      // Extract the translation key from {LANG_xxx.data-redirect} or {LANG_xxx}
      const match = dataRedirectValue.match(/\{LANG_([a-z0-9]+)(?:\.data-redirect)?\}/);
      if (match) {
        const translationKey = match[1];
        // Get the translation value from state
        const translation = getTranslationByKeyAndLang(translationKey, defaultLanguage);
        if (translation && translation.value) {
          // Restore the actual value to data-redirect attribute
          element.setAttribute('data-redirect', translation.value);
          // Remove the translation attribute if it exists
          element.removeAttribute(`${TRANSLATION_ATTRIBUTE}-data-redirect`);
          // Remove the translation from state
          removeTranslation(translationKey);
        }
      }
    }
  });
};

const IMAGE_WIDGET_CLASS = 'spm_widget_image';
// Attributes an image widget may carry as translations, per tag
const IMAGE_WIDGET_TRANSLATED_ATTRIBUTES: Record<string, string[]> = { a: ['href'], img: ['src', 'alt'] };

interface TranslatedAttribute {
  element: cheerio.Cheerio<cheerio.Node>;
  attr: string;
  key: string;
  value: string;
}

interface TranslatedImageFragment {
  $: cheerio.CheerioAPI;
  attributes: TranslatedAttribute[];
}

/**
 * Parse an html translation value expected to be exactly `<img>` or `<a><img></a>` whose
 * attributes still carry their original translation keys. Anything else returns null.
 */
const parseTranslatedImageFragment = (value: string): TranslatedImageFragment | null => {
  const $ = cheerio.load(value);
  const significant = (nodes: cheerio.Cheerio<cheerio.Node>) => nodes
    .filter((index, node) => node.nodeType === Node.ELEMENT_NODE || (node.nodeType === Node.TEXT_NODE && $(node).text().trim() !== ''));
  const tagOf = (node: cheerio.Node) => (node as cheerio.Element).tagName?.toLowerCase();

  const roots = significant($('body').contents());
  if (roots.length !== 1 || roots[0].nodeType !== Node.ELEMENT_NODE) return null;

  const elements: Record<string, cheerio.Cheerio<cheerio.Node>> = {};
  if (tagOf(roots[0]) === 'img') {
    elements.img = $(roots[0]);
  } else if (tagOf(roots[0]) === 'a') {
    const children = significant($(roots[0]).contents());
    if (children.length !== 1 || tagOf(children[0]) !== 'img') return null;
    elements.a = $(roots[0]);
    elements.img = $(children[0]);
  } else {
    return null;
  }

  const attributes: TranslatedAttribute[] = [];
  const valid = Object.keys(elements).every((tag) => {
    const element = elements[tag];
    return Object.keys(element.attr() ?? {})
      .filter((name) => name.startsWith(`${TRANSLATION_ATTRIBUTE}-`))
      .every((name) => {
        const attr = name.substring(TRANSLATION_ATTRIBUTE.length + 1);
        const match = (element.attr(name) ?? '').match(/^LANG_([a-z0-9]+\.([a-z-]+))$/);
        if (!IMAGE_WIDGET_TRANSLATED_ATTRIBUTES[tag].includes(attr) || !match || match[2] !== attr) return false;
        attributes.push({
          element, attr, key: match[1], value: element.attr(attr) ?? '',
        });
        return true;
      });
  });
  if (!valid || !attributes.some(({ attr }) => attr === 'src')) return null;

  return { $, attributes };
};

const unwrapImageWidgetSection = (section: Section): Section => {
  // Cheap gate: a wrapped image keeps its translated `src` marker inside the stored value, healthy sections never do
  const marker = `${TRANSLATION_ATTRIBUTE}-src`;
  if (section.type === 'design' || !section.translations?.includes(marker)) return section;

  // Same normalization as the Template constructor: stored values may hold raw line breaks
  const translations: Record<string, Record<string, any>> = JSON.parse(section.translations.replace(/(\r\n|\n|\r)/gm, ' '));
  const languages = Object.keys(translations);
  const defaultLanguage = languages.includes(getShopDefaultLang()) ? getShopDefaultLang() : languages[0];
  if (!defaultLanguage) return section;

  let { html } = section;
  const candidates = Object.keys(translations[defaultLanguage]).filter((key) => {
    const entry = translations[defaultLanguage][key];
    return entry?.type === 'html' && typeof entry.value === 'string' && entry.value.includes(marker) && html.split(`{LANG_${key}}`).length === 2;
  });
  if (!candidates.length) return section;

  const $section = cheerio.load(section.html);
  const cells = $section(`.${IMAGE_WIDGET_CLASS} > tbody > tr > th, .${IMAGE_WIDGET_CLASS} > tbody > tr > td`);
  const signatureOf = (fragment: TranslatedImageFragment) => fragment.attributes.map(({ attr, key }) => `${attr}=${key}`).sort().join('|');
  let changed = false;

  candidates.forEach((key) => {
    const token = `{LANG_${key}}`;
    if (cells.filter((index, cell) => $section(cell).html()?.trim() === token).length !== 1) return;

    const fragments = languages.map((language) => {
      const entry = translations[language]?.[key];
      return entry?.type === 'html' && typeof entry.value === 'string' ? parseTranslatedImageFragment(entry.value) : null;
    });
    if (fragments.some((fragment) => !fragment)) return;
    if (new Set(fragments.map((fragment) => signatureOf(fragment as TranslatedImageFragment))).size !== 1) return;

    // Never overwrite: the target base key must not already hold this attribute nor a markup translation
    const conflict = languages.some((language, index) => (fragments[index] as TranslatedImageFragment).attributes
      .some(({ key: attributeKey, attr }) => {
        const existing = translations[language][attributeKey.split('.')[0]];
        return existing !== undefined && (existing.value !== undefined || existing[attr] !== undefined);
      }));
    if (conflict) return;

    languages.forEach((language, index) => {
      delete translations[language][key];
      (fragments[index] as TranslatedImageFragment).attributes.forEach(({ attr, key: attributeKey, value }) => {
        const base = attributeKey.split('.')[0];
        const data = elementAttributes.find((elementAttribute) => elementAttribute.name === attr)?.data(value) ?? {};
        translations[language][base] = { ...translations[language][base], ...data };
      });
    });

    const fragment = fragments[languages.indexOf(defaultLanguage)] as TranslatedImageFragment;
    fragment.attributes.forEach(({ element, attr, key: attributeKey }) => {
      element.removeAttr(`${TRANSLATION_ATTRIBUTE}-${attr}`);
      element.attr(attr, `{LANG_${attributeKey}}`);
    });
    html = html.replace(token, () => fragment.$('body').html() ?? '');
    changed = true;
  });

  return changed ? { ...section, html, translations: JSON.stringify(translations) } : section;
};

/**
 * Fix retroactive (SC-3848): image widgets whose cell got wrapped into a single `html` translation
 * keep their original attribute keys inside the value. Restore the attribute translations (per language)
 * and the `{LANG_key.attr}` tokens in the section html. Sections are left untouched on any doubt,
 * including unexpected data shapes: a repair must never prevent the template from loading.
 */
export const unwrapImageWidgetTranslations = (sections: Section[]): Section[] => sections.map((section) => {
  try {
    return unwrapImageWidgetSection(section);
  } catch (e) {
    return section;
  }
});
